/**
 * Product Hierarchy Utilities
 * Handles hierarchical product category navigation and leaf product extraction
 */

import productsConfig from '../config/productsConfig';

/**
 * Get top-level categories from product config
 * Returns array of category objects
 */
export const getTopLevelCategories = () => {
  if (!productsConfig?.products) {
    return [];
  }

  return productsConfig.products.map(category => ({
    id: category.category.slug,
    label: category.category.display_name,
    type: category.category.type,
    hasChildren: category.items && category.items.length > 0
  }));
};

/**
 * Get children of a selected category/item
 * @param {string} categorySlug - The slug of the selected category
 * @param {Array} path - Array of selected slugs representing the path
 * @returns {Array} Array of child items/categories
 */
export const getChildren = (categorySlug, path = []) => {
  if (!productsConfig?.products) {
    return [];
  }

  // Navigate through the hierarchy based on path
  let currentLevel = productsConfig.products;
  
  // Follow the path to find the current level
  for (const pathSlug of path) {
    const found = currentLevel.find(item => 
      item.category?.slug === pathSlug || item.display_name || item.slug === pathSlug
    );
    
    if (found && found.items) {
      currentLevel = found.items;
    } else {
      return [];
    }
  }

  // If we're looking for children of a specific item, find it first
  if (categorySlug) {
    const found = currentLevel.find(item => {
      if (item.category) {
        return item.category.slug === categorySlug;
      }
      return item.slug === categorySlug;
    });

    if (!found || !found.items) {
      return [];
    }
    
    currentLevel = found.items;
  }

  // Return children with consistent structure
  return currentLevel.map(item => {
    if (item.category) {
      // It's a category/subcategory
      return {
        id: item.category.slug,
        label: item.category.display_name,
        type: item.category.type || 'category',
        hasChildren: item.items && item.items.length > 0,
        isLeaf: false
      };
    } else if (item.display_name && item.slug) {
      // It's a leaf product
      return {
        id: item.slug,
        label: item.display_name,
        type: 'product',
        hasChildren: false,
        isLeaf: true,
        main_product_type: item.display_name,
        main_product_type_slug: item.slug
      };
    }
    return null;
  }).filter(Boolean);
};

/**
 * Navigate through product hierarchy using a selection path
 * Returns the current level items based on the path
 */
export const navigateHierarchy = (path = []) => {
  if (!productsConfig?.products) {
    return [];
  }

  if (path.length === 0) {
    return getTopLevelCategories();
  }

  let currentLevel = productsConfig.products;
  const navigationPath = [...path];

  // Navigate through each level
  while (navigationPath.length > 0) {
    const currentSlug = navigationPath.shift();
    const found = currentLevel.find(item => {
      if (item.category) {
        return item.category.slug === currentSlug;
      }
      return item.slug === currentSlug;
    });

    if (!found) {
      return [];
    }

    // If this is a leaf product, return it
    if (found.display_name && found.slug && !found.items) {
      return [{
        id: found.slug,
        label: found.display_name,
        type: 'product',
        hasChildren: false,
        isLeaf: true,
        main_product_type: found.display_name,
        main_product_type_slug: found.slug
      }];
    }

    // Move to next level if items exist
    if (found.items) {
      currentLevel = found.items;
    } else {
      return [];
    }
  }

  // Return current level items
  return currentLevel.map(item => {
    if (item.category) {
      return {
        id: item.category.slug,
        label: item.category.display_name,
        type: item.category.type || 'category',
        hasChildren: item.items && item.items.length > 0,
        isLeaf: false
      };
    } else if (item.display_name && item.slug) {
      return {
        id: item.slug,
        label: item.display_name,
        type: 'product',
        hasChildren: false,
        isLeaf: true,
        main_product_type: item.display_name,
        main_product_type_slug: item.slug
      };
    }
    return null;
  }).filter(Boolean);
};

/**
 * Check if a selection path leads to a leaf product
 * @param {Array} path - Array of selected slugs
 * @returns {Object|null} Leaf product info or null
 */
export const getLeafProductFromPath = (path = []) => {
  if (!path || path.length === 0) {
    return null;
  }

  if (!productsConfig?.products) {
    return null;
  }

  let currentLevel = productsConfig.products;
  let lastItem = null;

  // Navigate through the path
  for (const slug of path) {
    const found = currentLevel.find(item => {
      if (item.category) {
        return item.category.slug === slug;
      }
      return item.slug === slug;
    });

    if (!found) {
      return null;
    }

    lastItem = found;

    // If this item has no children, it's a leaf
    if (!found.items) {
      if (found.display_name && found.slug) {
        return {
          main_product_type: found.display_name,
          main_product_type_slug: found.slug
        };
      }
      return null;
    }

    // Move to next level
    currentLevel = found.items;
  }

  // If we've reached the end and lastItem is a product
  if (lastItem && lastItem.display_name && lastItem.slug && !lastItem.items) {
    return {
      main_product_type: lastItem.display_name,
      main_product_type_slug: lastItem.slug
    };
  }

  return null;
};

/**
 * Find path of slugs from root to a leaf product by its main_product_type_slug.
 * Used when hydrating Edit Enquiry form to restore product selection.
 * @param {string} leafSlug - main_product_type_slug of the leaf product
 * @param {Array} level - current level in config (products or items)
 * @param {Array} pathSoFar - accumulated path of slugs
 * @returns {Array|null} Path of slugs from root to leaf, or null if not found
 */
const findPathToLeaf = (leafSlug, level, pathSoFar = []) => {
  if (!level || !Array.isArray(level)) return null;
  for (const item of level) {
    const slug = item.category?.slug ?? item.slug;
    if (!slug) continue;
    const newPath = [...pathSoFar, slug];
    // Leaf product: has display_name and slug, no items
    if (item.display_name && item.slug && !item.items) {
      if (item.slug === leafSlug) return newPath;
      continue;
    }
    // Category/subcategory: recurse into items
    if (item.items && item.items.length > 0) {
      const found = findPathToLeaf(leafSlug, item.items, newPath);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Get selection path (array of slugs) for a given main_product_type_slug.
 * Returns path from root to that leaf for use in product dropdowns (e.g. Edit mode).
 */
export const getPathToLeafSlug = (mainProductTypeSlug) => {
  if (!mainProductTypeSlug || !productsConfig?.products) return [];
  const path = findPathToLeaf(mainProductTypeSlug, productsConfig.products);
  return path || [];
};

